import axios from 'axios'
import { useAuthStore } from '../stores/authStore'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.dashit.io',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try direct localStorage FIRST (most reliable), then zustand
    let token = null
    try {
      token = localStorage.getItem('token')
    } catch (e) {
      console.error('Failed to read token from localStorage:', e)
    }
    
    // Fallback to zustand if localStorage didn't have it
    if (!token) {
      token = useAuthStore.getState().token
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      console.log('Auth header added:', token.substring(0, 20) + '...')
    } else {
      console.log('No token found for request')
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't logout for service-specific auth failures (email, calendar, spotify sync)
      const url = error.config?.url || ''
      const isServiceAuth = url.includes('/email/sync') || 
                           url.includes('/calendar/sync') || 
                           url.includes('/spotify/')
      
      if (!isServiceAuth) {
        useAuthStore.getState().logout()
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Auth APIs
export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  register: (data: any) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
}

// Dashboard APIs
export const dashboardAPI = {
  getAll: () => api.get('/dashboards'),
  getById: (id: number) => api.get(`/dashboards/${id}`),
  create: (data: any) => api.post('/dashboards', data),
  update: (id: number, data: any) => api.put(`/dashboards/${id}`, data),
  delete: (id: number) => api.delete(`/dashboards/${id}`),
}

// Tab APIs
export const tabAPI = {
  getByDashboard: (dashboardId: number) => api.get(`/dashboards/${dashboardId}/tabs`),
  create: (dashboardId: number, data: any) => api.post(`/dashboards/${dashboardId}/tabs`, data),
  update: (id: number, data: any) => api.put(`/tabs/${id}`, data),
  delete: (id: number) => api.delete(`/tabs/${id}`),
  reorder: (id: number, positions: any) => api.post(`/tabs/${id}/reorder`, positions),
  duplicate: (id: number) => api.post(`/tabs/${id}/duplicate`, {}),
}

// Widget APIs
export const widgetAPI = {
  getByTab: (tabId: number) => api.get(`/tabs/${tabId}/widgets`),
  create: (tabId: number, data: any) => api.post(`/tabs/${tabId}/widgets`, data),
  update: (id: number, data: any) => api.put(`/widgets/${id}`, data),
  delete: (id: number) => api.delete(`/widgets/${id}`),
  expand: (id: number) => api.post(`/widgets/${id}/expand`),
  collapse: (id: number) => api.post(`/widgets/${id}/collapse`),
  reorder: (widgets: any) => api.post('/widgets/reorder', { widgets }),
}

// App APIs
export const appAPI = {
  getAll: () => api.get('/apps'),
  getBySlug: (slug: string) => api.get(`/apps/${slug}`),
  install: (data: any) => api.post('/apps/install', data),
}

// Task APIs
export const taskAPI = {
  getAll: (dashboardId?: number) => api.get('/tasks', { params: { dashboard_id: dashboardId } }),
  create: (data: any) => api.post('/tasks', data),
  update: (id: number, data: any) => api.put(`/tasks/${id}`, data),
  delete: (id: number) => api.delete(`/tasks/${id}`),
  complete: (id: number) => api.post(`/tasks/${id}/complete`),
  assign: (id: number, userId: number) => api.post(`/tasks/${id}/assign`, { assigned_to: userId }),
}

// Calendar APIs
export const calendarAPI = {
  getEvents: (start: string, end: string) => api.get('/calendar/events', { params: { start, end } }),
  createEvent: (data: any) => api.post('/calendar/events', data),
  updateEvent: (id: number, data: any) => api.put(`/calendar/events/${id}`, data),
  deleteEvent: (id: number) => api.delete(`/calendar/events/${id}`),
  // OAuth & Sync
  getGoogleAuthUrl: () => api.get('/oauth/google'),
  getOutlookAuthUrl: () => api.get('/oauth/outlook'),
  getConnections: () => api.get('/calendar/connections'),
  syncCalendar: (provider: string) => api.post(`/calendar/sync/${provider}`),
  disconnectCalendar: (provider: string) => api.delete(`/calendar/connections/${provider}`),
}

// Email APIs
export const emailAPI = {
  getAll: (params?: { limit?: number; offset?: number; category?: string }) => api.get('/emails', { params }),
  markRead: (id: string, isRead: boolean) => api.patch(`/emails/${id}/read`, { read: isRead }),
  star: (id: string, isStarred: boolean) => api.patch(`/emails/${id}/star`, { starred: isStarred }),
  delete: (id: string) => api.delete(`/emails/${id}`),
  // OAuth & Sync
  getGmailAuthUrl: () => api.get('/oauth/gmail'),
  getOutlookEmailAuthUrl: () => api.get('/oauth/outlook-email'),
  getConnections: () => api.get('/email/connections'),
  // Removed syncEmails - using API-only approach
  disconnectEmail: (provider: string) => api.delete(`/email/connections/${provider}`),
}

// Note APIs
export const noteAPI = {
  getAll: () => api.get('/notes'),
  create: (data: any) => api.post('/notes', data),
  update: (id: number, data: any) => api.put(`/notes/${id}`, data),
  delete: (id: number) => api.delete(`/notes/${id}`),
}

// Bookmark APIs
export const bookmarkAPI = {
  getAll: (folder?: string) => api.get('/bookmarks', { params: { folder } }),
  getFolders: () => api.get('/bookmarks/folders'),
  create: (data: any) => api.post('/bookmarks', data),
  update: (id: number, data: any) => api.put(`/bookmarks/${id}`, data),
  delete: (id: number) => api.delete(`/bookmarks/${id}`),
  createFolder: (name: string) => api.post('/bookmarks/folders', { name }),
  deleteFolder: (name: string) => api.delete('/bookmarks/folders', { data: { name } }),
}

// RSS Feed APIs
export const rssAPI = {
  getFeeds: () => api.get('/rss/feeds'),
  subscribe: (feedUrl: string) => api.post('/rss/feeds', { feed_url: feedUrl }),
  getFeedItems: (feedId: number) => api.get(`/rss/feeds/${feedId}/items`),
  getAllItems: () => api.get('/rss/items'),
  refresh: (feedId: number) => api.post(`/rss/feeds/${feedId}/refresh`),
  unsubscribe: (feedId: number) => api.delete(`/rss/feeds/${feedId}`),
}

// Reddit APIs
export const redditAPI = {
  getPosts: (subreddit?: string, limit?: number) => 
    api.get('/reddit/posts', { params: { subreddit, limit } }),
  getPopularSubreddits: () => api.get('/reddit/subreddits'),
}

// Sports Scores APIs
export const sportsAPI = {
  getScores: (sport?: string, league?: string, date?: string) =>
    api.get('/sports/scores', { params: { sport, league, date } }),
  getLiveGames: () => api.get('/sports/live'),
  getFavoriteGames: () => api.get('/sports/favorites/games'),
  getLeagues: () => api.get('/sports/leagues'),
  getTeams: (sport: string, league: string) => api.get('/sports/teams', { params: { sport, league } }),
  getFavorites: () => api.get('/sports/favorites'),
  addFavorite: (teamId: number) => api.post('/sports/favorites', { team_id: teamId }),
  removeFavorite: (teamId: number) => api.delete(`/sports/favorites/${teamId}`),
  refreshScores: (sport?: string, league?: string) =>
    api.post('/sports/refresh', {}, { params: { sport, league } }),
}

// Weight Tracking APIs
export const weightAPI = {
  getEntries: () => api.get('/weight/entries'),
  saveEntry: (data: { weight: number; unit: string; entry_date: string; notes?: string }) =>
    api.post('/weight/entries', data),
  updateEntry: (entryId: number, data: { weight: number; unit: string; entry_date: string; notes?: string }) =>
    api.put(`/weight/entries/${entryId}`, data),
  deleteEntry: (entryId: number) => api.delete(`/weight/entries/${entryId}`),
  getStats: () => api.get('/weight/stats'),
}

// Fitness/Workout Tracking APIs
export const fitnessAPI = {
  getGoal: () => api.get('/fitness/goal'),
  setGoal: (weeklyTarget: number) => api.post('/fitness/goal', { weekly_target: weeklyTarget }),
  getExercises: () => api.get('/fitness/exercises'),
  addExercise: (name: string, category?: string) => api.post('/fitness/exercises', { name, category }),
  deleteExercise: (exerciseId: number) => api.delete(`/fitness/exercises/${exerciseId}`),
  getWorkouts: (limit?: number) => api.get('/fitness/workouts', { params: { limit } }),
  getWorkout: (workoutId: number) => api.get(`/fitness/workouts/${workoutId}`),
  createWorkout: (data: any) => api.post('/fitness/workouts', data),
  updateWorkout: (workoutId: number, data: any) => api.put(`/fitness/workouts/${workoutId}`, data),
  deleteWorkout: (workoutId: number) => api.delete(`/fitness/workouts/${workoutId}`),
  getStats: () => api.get('/fitness/stats'),
}

// Wind & Tide APIs
export const windTideAPI = {
  getData: (zipCode: string) => api.get('/wind-tide/data', { params: { zip_code: zipCode } }),
  getLocations: () => api.get('/wind-tide/locations'),
  saveLocation: (data: any) => api.post('/wind-tide/locations', data),
  deleteLocation: (id: number) => api.delete(`/wind-tide/locations/${id}`),
}

// Weather APIs
export const weatherAPI = {
  getByZip: (zipCode: string) => api.get(`/weather/${zipCode}`),
}

// Theme APIs
export const themeAPI = {
  getAll: () => api.get('/themes'),
  getCurrent: () => api.get('/themes/current'),
  getById: (id: number) => api.get(`/themes/${id}`),
  create: (data: any) => api.post('/themes', data),
  update: (id: number, data: any) => api.put(`/themes/${id}`, data),
  delete: (id: number) => api.delete(`/themes/${id}`),
  duplicate: (id: number) => api.post(`/themes/${id}/duplicate`, {}),
  setUserTheme: (themeId: number) => api.post('/user/theme', { theme_id: themeId }),
}

// Voice APIs
export const voiceAPI = {
  transcribe: (formData: FormData) => {
    return api.post('/voice/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  process: (transcript: string) => api.post('/voice/process', { transcript }),
}

// Spotify APIs
export const spotifyAPI = {
  getAuthUrl: () => api.get('/spotify/auth-url'),
  getStatus: () => api.get('/spotify/status'),
  disconnect: () => api.post('/spotify/disconnect'),
  getCurrentPlayback: () => api.get('/spotify/playback'),
  getRecentlyPlayed: () => api.get('/spotify/recently-played'),
}

// User Profile APIs
export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data: any) => api.put('/user/profile', data),
  changeEmail: (data: { new_email: string; password: string }) => api.post('/user/change-email', data),
  changePassword: (data: { current_password: string; new_password: string; confirm_password: string }) => 
    api.post('/user/change-password', data),
  uploadAvatar: (file: File) => {
    const formData = new FormData()
    formData.append('avatar', file)
    return api.post('/user/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  deleteAccount: (data: { password: string; confirmation: string }) => api.delete('/user/account', { data }),
}

// Admin APIs
export const adminAPI = {
  getUsers: (page: number = 1) => api.get('/admin/users', { params: { page } }),
  getAdmins: (page: number = 1) => api.get('/admin/admins', { params: { page } }),
  getUser: (id: number) => api.get(`/admin/users/${id}`),
  updateUser: (id: number, data: any) => api.put(`/admin/users/${id}`, data),
  suspendUser: (id: number) => api.post(`/admin/users/${id}/suspend`),
  activateUser: (id: number) => api.post(`/admin/users/${id}/activate`),
  deleteUser: (id: number) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id: number, newPassword: string) => api.post(`/admin/users/${id}/reset-password`, { password: newPassword }),
  createAdmin: (data: any) => api.post('/admin/admins', data),
  getAnalytics: () => api.get('/admin/analytics'),
  getPendingApps: () => api.get('/admin/apps/pending'),
  approveApp: (id: number) => api.post(`/admin/apps/${id}/approve`),
  rejectApp: (id: number, reason: string) => api.post(`/admin/apps/${id}/reject`, { reason }),
  getCoupons: () => api.get('/admin/coupons'),
  createCoupon: (data: any) => api.post('/admin/coupons', data),
}

// Developer APIs
export const developerAPI = {
  getMyApps: () => api.get('/developer/apps'),
  submitApp: (data: any) => api.post('/developer/apps', data),
  updateApp: (id: number, data: any) => api.put(`/developer/apps/${id}`, data),
  deleteApp: (id: number) => api.delete(`/developer/apps/${id}`),
  getApiKeys: () => api.get('/developer/api-keys'),
  createApiKey: (data: any) => api.post('/developer/api-keys', data),
  revokeApiKey: (id: number) => api.delete(`/developer/api-keys/${id}`),
  getAnalytics: () => api.get('/developer/analytics'),
}

// Widget Permission APIs
export const permissionAPI = {
  getWidgetPermissions: (widgetId: number) => api.get(`/widgets/${widgetId}/permissions`),
  grantPermission: (widgetId: number, permissionType: string) => 
    api.post(`/widgets/${widgetId}/permissions/grant`, { permission_type: permissionType }),
  bulkGrantPermissions: (widgetId: number, permissions: string[]) => 
    api.post(`/widgets/${widgetId}/permissions/bulk-grant`, { permissions }),
  revokePermission: (widgetId: number, permissionType: string) => 
    api.delete(`/widgets/${widgetId}/permissions/revoke`, { data: { permission_type: permissionType } }),
  getUserPermissions: () => api.get('/user/permissions'),
}

// Widget API Proxy
export const widgetProxyAPI = {
  proxyRequest: (widgetId: number, url: string, method: string = 'GET', data?: any, headers?: any) =>
    api.post('/widget-proxy', { widgetId, url, method, data, headers }),
  getWidgetApiStats: (widgetId: number) => api.get(`/widgets/${widgetId}/api-stats`),
}

// Developer Application APIs
export const developerApplicationAPI = {
  submit: (data: any) => api.post('/developer-application/submit', data),
  getMyApplication: () => api.get('/developer-application/my-application'),
  getAll: () => api.get('/admin/developer-applications'),
  getPending: () => api.get('/admin/developer-applications/pending'),
  approve: (applicationId: number) => api.post('/admin/developer-applications/approve', { application_id: applicationId }),
  reject: (applicationId: number, reason: string) => api.post('/admin/developer-applications/reject', { application_id: applicationId, reason }),
}

// Two-Factor Authentication APIs
export const twoFactorAPI = {
  getStatus: () => api.get('/2fa/status'),
  setup: () => api.post('/2fa/setup'),
  verify: (code: string) => api.post('/2fa/verify', { code }),
  disable: (password: string) => api.post('/2fa/disable', { password }),
  verifyLogin: (userId: number, code: string) => api.post('/auth/verify-2fa', { user_id: userId, code }),
}

// Email Privacy APIs
export const emailPrivacyAPI = {
  getSettings: (widgetId: number) => api.get(`/widgets/${widgetId}/privacy`),
  updateSettings: (widgetId: number, data: { enabled?: boolean; timeout?: number; passcode?: string }) => 
    api.put(`/widgets/${widgetId}/privacy`, data),
  verifyPasscode: (widgetId: number, passcode: string) => 
    api.post(`/widgets/${widgetId}/privacy/verify`, { passcode }),
  resetPasscode: (widgetId: number, password: string, newPasscode: string) => 
    api.post(`/widgets/${widgetId}/privacy/reset`, { password, new_passcode: newPasscode }),
  disablePrivacy: (widgetId: number, password: string) => 
    api.post(`/widgets/${widgetId}/privacy/disable`, { password }),
}

export default api

